/**
 * Paper Map Viewer Component
 * 
 * Displays exam paper images with detected question regions overlaid.
 * Allows selection and visual inspection of regions.
 */

import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Slider,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';

interface Region {
  id: string;
  question_id: string;
  question_label: string | null;
  page_index: number;
  bbox_x: number;
  bbox_y: number;
  bbox_width: number;
  bbox_height: number;
  confidence: number;
}

interface PaperMapViewerProps {
  imageUrls: string[];
  regions: Region[];
  selectedQuestionId: string | null;
  onRegionSelect?: (questionId: string) => void;
}

export function PaperMapViewer({
  imageUrls,
  regions,
  selectedQuestionId,
  onRegionSelect,
}: PaperMapViewerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Get regions for current page
  const pageRegions = regions.filter((r) => r.page_index === currentPage);

  // Handle image load to get dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageDimensions({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      });
    }
  };

  // Navigate to page containing selected question
  useEffect(() => {
    if (selectedQuestionId) {
      const region = regions.find((r) => r.question_id === selectedQuestionId);
      if (region && region.page_index !== currentPage) {
        setCurrentPage(region.page_index);
      }
    }
  }, [selectedQuestionId, regions]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 25));
  const handleFitToScreen = () => setZoom(100);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(0, p - 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(imageUrls.length - 1, p + 1));

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.9) return 'rgba(76, 175, 80, 0.5)'; // Green
    if (confidence >= 0.7) return 'rgba(255, 193, 7, 0.5)'; // Yellow
    return 'rgba(244, 67, 54, 0.5)'; // Red
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          原卷預覽
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Page navigation */}
        {imageUrls.length > 1 && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handlePrevPage} disabled={currentPage === 0}>
              <PrevIcon />
            </IconButton>
            <Chip
              label={`${currentPage + 1} / ${imageUrls.length}`}
              size="small"
            />
            <IconButton
              onClick={handleNextPage}
              disabled={currentPage === imageUrls.length - 1}
            >
              <NextIcon />
            </IconButton>
          </Stack>
        )}

        {/* Zoom controls */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={handleZoomOut}>
            <ZoomOutIcon />
          </IconButton>
          <Slider
            value={zoom}
            onChange={(_, v) => setZoom(v as number)}
            min={25}
            max={300}
            sx={{ width: 100 }}
          />
          <IconButton onClick={handleZoomIn}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleFitToScreen}>
            <FitScreenIcon />
          </IconButton>
          <Chip label={`${zoom}%`} size="small" variant="outlined" />
        </Stack>
      </Stack>

      {/* Image container */}
      <Box
        ref={containerRef}
        sx={{
          overflow: 'auto',
          maxHeight: '60vh',
          position: 'relative',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top left',
          }}
        >
          {imageUrls[currentPage] && (
            <img
              ref={imageRef}
              src={imageUrls[currentPage]}
              alt={`Page ${currentPage + 1}`}
              onLoad={handleImageLoad}
              style={{
                display: 'block',
                maxWidth: 'none',
              }}
            />
          )}

          {/* Region overlays */}
          {pageRegions.map((region) => {
            const isSelected = region.question_id === selectedQuestionId;
            return (
              <Tooltip
                key={region.id}
                title={`${region.question_id} (信心度: ${Math.round(region.confidence * 100)}%)`}
              >
                <Box
                  onClick={() => onRegionSelect?.(region.question_id)}
                  sx={{
                    position: 'absolute',
                    left: `${region.bbox_x * 100}%`,
                    top: `${region.bbox_y * 100}%`,
                    width: `${region.bbox_width * 100}%`,
                    height: `${region.bbox_height * 100}%`,
                    border: isSelected
                      ? '3px solid #1976d2'
                      : '2px solid rgba(0, 0, 0, 0.5)',
                    borderRadius: 1,
                    bgcolor: isSelected
                      ? 'rgba(25, 118, 210, 0.2)'
                      : getConfidenceColor(region.confidence),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.3)',
                      borderColor: '#1976d2',
                    },
                  }}
                >
                  {/* Question label */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: -20,
                      left: 0,
                      bgcolor: isSelected ? 'primary.main' : 'grey.800',
                      color: 'white',
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: '0.7rem',
                    }}
                  >
                    {region.question_id}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              bgcolor: 'rgba(76, 175, 80, 0.5)',
              border: '1px solid rgba(0,0,0,0.3)',
            }}
          />
          <Typography variant="caption">高信心度 (&gt;90%)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              bgcolor: 'rgba(255, 193, 7, 0.5)',
              border: '1px solid rgba(0,0,0,0.3)',
            }}
          />
          <Typography variant="caption">中信心度 (70-90%)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              bgcolor: 'rgba(244, 67, 54, 0.5)',
              border: '1px solid rgba(0,0,0,0.3)',
            }}
          />
          <Typography variant="caption">低信心度 (&lt;70%)</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

